// What a project dashboard flags (components/projects/dashboard): what's
// overdue or late, what's coming up, the critical path, what we're waiting on
// from outside, and who has how much on. Pure, so the same figures serve the
// Projects page (every project) and a project's Overview tab (one).
//
// Only "leaf" topics count as work — a branch is the sum of what's under it,
// and counting both would count the same work twice.
import { TaskItem } from '../types/tasks';
import { addDays, daysBetween, mondayOf } from './taskDates';
import { hasConflict, isCritical, isLate } from './projectPlan';

const open = (i: TaskItem) => i.progress < 100;

/** Topics with nothing under them: the actual work. */
export function leavesOf(items: TaskItem[]): TaskItem[] {
  const parents = new Set(items.map((i) => i.parent_id));
  return items.filter((i) => !parents.has(i.id) && !i.linked_to);
}

/**
 * Where a topic sits, for a dashboard line: the project (when the dashboard
 * covers several) and the main branch it's under, e.g. "Race car › Bodywork".
 * A name like "Finish design" means little without its branch. A main branch
 * itself gets just the project.
 */
export function whereLabel(item: TaskItem, byId: Map<number, TaskItem>, projectTitle?: string): string | undefined {
  let top: TaskItem | undefined = item;
  for (let guard = 0; top && top.parent_id !== null && guard < 20; guard++) top = byId.get(top.parent_id);
  const branch = top && top.id !== item.id ? top.text || 'Topic' : undefined;
  const parts = [projectTitle, branch].filter((p): p is string => !!p);
  return parts.length ? parts.join(' › ') : undefined;
}

export interface Flag {
  item: TaskItem;
  /** The date it's about (due date, follow-up, planned start…). */
  date: string | null;
  label: string;
  tone: 'danger' | 'warn' | 'info' | 'muted' | 'event';
}

/**
 * Past their due date, or planned to miss something fixed — worst first —
 * then dates that clash (pinned before what they wait for is done).
 */
export function overdueAndLate(items: TaskItem[], today: string): Flag[] {
  const out: Flag[] = [];
  for (const i of leavesOf(items)) {
    if (!open(i)) continue;
    if (i.due_date && i.due_date < today) {
      const days = daysBetween(i.due_date, today);
      out.push({ item: i, date: i.due_date, label: `Overdue ${days}d`, tone: 'danger' });
    } else if (isLate(i)) {
      out.push({ item: i, date: i.plan_finish ?? null, label: `${-i.slack_days!}d late`, tone: 'danger' });
    } else if (hasConflict(i)) {
      out.push({ item: i, date: i.plan_start ?? null, label: `Conflict · ${i.conflict_days}d early`, tone: 'warn' });
    }
  }
  const weight = (f: Flag) =>
    f.label.startsWith('Overdue') ? daysBetween(f.date!, today) + 1000 : f.tone === 'warn' ? -1000 : -(f.item.slack_days ?? 0);
  return out.sort((a, b) => weight(b) - weight(a));
}

/**
 * The next `days` days: events and milestones, due dates, and tasks planned
 * to start. Soonest first.
 */
export function comingUp(items: TaskItem[], today: string, days = 14): Flag[] {
  const end = addDays(today, days);
  const inWindow = (d: string | null | undefined): d is string => !!d && d >= today && d <= end;
  const parents = new Set(items.map((i) => i.parent_id));
  const out: Flag[] = [];
  for (const i of items) {
    if (!open(i) || i.linked_to) continue;
    const kind = i.node_kind || 'task';
    if (kind === 'event' || kind === 'milestone') {
      // An event that's under way counts until it ends.
      const date = kind === 'event' && i.plan_start && i.plan_start < today && inWindow(i.plan_finish) ? today : i.plan_start;
      if (inWindow(date)) out.push({ item: i, date, label: kind === 'event' ? 'Event' : 'Milestone', tone: 'event' });
      continue;
    }
    if (inWindow(i.due_date)) {
      out.push({ item: i, date: i.due_date, label: 'Due', tone: daysBetween(today, i.due_date) <= 1 ? 'warn' : 'info' });
    } else if (inWindow(i.plan_start) && i.progress === 0 && !parents.has(i.id)) {
      out.push({ item: i, date: i.plan_start, label: 'Starts', tone: 'muted' });
    }
  }
  return out.sort((a, b) => a.date!.localeCompare(b.date!) || toneRank(a.tone) - toneRank(b.tone));
}

const toneRank = (t: Flag['tone']) => ['event', 'danger', 'warn', 'info', 'muted'].indexOf(t);

/** Open work on the critical path (slack 0 or less), in the order it happens. */
export function criticalPath(items: TaskItem[]): TaskItem[] {
  return leavesOf(items)
    .filter((i) => i.plan_start && isCritical(i))
    .sort((a, b) => a.plan_start!.localeCompare(b.plan_start!) || a.plan_finish!.localeCompare(b.plan_finish!));
}

/** When the project is planned to be done (the latest planned finish), or null. */
export function plannedFinish(items: TaskItem[]): string | null {
  let last: string | null = null;
  for (const i of items) if (open(i) && i.plan_finish && (!last || i.plan_finish > last)) last = i.plan_finish;
  return last;
}

/** Waiting on someone outside: follow-ups due first. */
export function waitingOn(items: TaskItem[], today: string): Flag[] {
  return items
    .filter((i) => open(i) && !i.linked_to && i.waiting_on)
    .map((i): Flag => {
      const due = !!i.follow_up_date && i.follow_up_date <= today;
      return { item: i, date: i.follow_up_date, label: i.waiting_on!, tone: due ? 'warn' : 'muted' };
    })
    .sort((a, b) => (a.date || '9999').localeCompare(b.date || '9999'));
}

export interface Workload {
  /** '' = nobody assigned. */
  email: string;
  open: number;
  overdue: number;
  critical: number;
  /** Planned tasks overlapping each of the coming weeks (Monday first). */
  weeks: number[];
  /** The tasks behind each number, so a cell can list them. */
  tasks: { open: TaskItem[]; overdue: TaskItem[]; critical: TaskItem[]; weeks: TaskItem[][] };
}

/**
 * Who has how much on: open work per person (each task counts for everyone
 * on it), and how many planned tasks fall in each of the next `weeks` weeks.
 */
export function workload(items: TaskItem[], today: string, weeks = 6): { weekStarts: string[]; people: Workload[] } {
  const first = mondayOf(today);
  const weekStarts = Array.from({ length: weeks }, (_, w) => addDays(first, w * 7));
  const rows = new Map<string, Workload>();
  const row = (email: string) => {
    let r = rows.get(email);
    if (!r) {
      r = {
        email, open: 0, overdue: 0, critical: 0, weeks: weekStarts.map(() => 0),
        tasks: { open: [], overdue: [], critical: [], weeks: weekStarts.map(() => []) },
      };
      rows.set(email, r);
    }
    return r;
  };
  for (const i of leavesOf(items)) {
    if (!open(i) || i.node_kind === 'event' || i.node_kind === 'milestone') continue;
    for (const email of i.assignees.length ? i.assignees : ['']) {
      const r = row(email);
      r.open++;
      r.tasks.open.push(i);
      if (i.due_date && i.due_date < today) {
        r.overdue++;
        r.tasks.overdue.push(i);
      }
      if (i.plan_start && isCritical(i)) {
        r.critical++;
        r.tasks.critical.push(i);
      }
      if (i.plan_start && i.plan_finish) {
        weekStarts.forEach((ws, w) => {
          if (i.plan_start! <= addDays(ws, 6) && i.plan_finish! >= ws) {
            r.weeks[w]++;
            r.tasks.weeks[w].push(i);
          }
        });
      }
    }
  }
  const people = Array.from(rows.values()).sort(
    (a, b) => (a.email === '' ? 1 : b.email === '' ? -1 : b.open - a.open)
  );
  return { weekStarts, people };
}
